import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Connection, PublicKey } from '@solana/web3.js';
import { shortenAddress } from '../lib/utils';
import {
  TrackedWallet,
  addTrackedWallet,
  removeTrackedWallet,
  syncConnectedWallet,
  isValidSolanaAddress,
} from '../lib/wallets';

interface TokenHolding {
  mint: string;
  symbol: string;
  name: string;
  balance: number;
  decimals: number;
  usdValue: number;
  priceChange24h: number;
  image?: string;
}

interface WalletPortfolio {
  address: string;
  label: string;
  isConnected: boolean;
  solBalance: number;
  holdings: TokenHolding[];
  totalValue: number;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.skop.site';
const connection = new Connection(`${API_BASE}/api/rpc`, 'confirmed');

export function Portfolio() {
  const navigate = useNavigate();
  const { publicKey, connected } = useWallet();
  const { setVisible } = useWalletModal();

  const [trackedWallets, setTrackedWallets] = useState<TrackedWallet[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<string | null>(null);
  const [holdings, setHoldings] = useState<TokenHolding[]>([]);
  const [solBalance, setSolBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [newWalletLabel, setNewWalletLabel] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  // Sync connected wallet with tracked wallets
  useEffect(() => {
    const wallets = syncConnectedWallet(publicKey?.toString() || null);
    setTrackedWallets(wallets);
    // Select connected wallet by default if available
    if (publicKey && !selectedWallet) {
      setSelectedWallet(publicKey.toString());
    }
  }, [connected, publicKey]);

  // Fetch portfolio for a single wallet address
  const fetchWalletPortfolio = useCallback(async (address: string): Promise<WalletPortfolio | null> => {
    try {
      const pubkey = new PublicKey(address);

      // Fetch SOL balance
      const balance = await connection.getBalance(pubkey);
      const solBalanceAmount = balance / 1e9;

      // Fetch token accounts
      const tokenAccounts = await connection.getParsedTokenAccountsByOwner(pubkey, {
        programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'),
      });

      // Process token holdings
      const holdingsList: TokenHolding[] = [];

      for (const account of tokenAccounts.value) {
        const parsed = account.account.data.parsed;
        const info = parsed.info;
        const tokenBalance = info.tokenAmount.uiAmount;

        // Skip dust balances
        if (tokenBalance < 0.01) continue;

        // Mock price data (replace with real API)
        const mockPrice = Math.random() * 0.001;
        const mockChange = (Math.random() - 0.5) * 100;

        holdingsList.push({
          mint: info.mint,
          symbol: shortenAddress(info.mint, 3).toUpperCase(),
          name: `Token ${shortenAddress(info.mint, 4)}`,
          balance: tokenBalance,
          decimals: info.tokenAmount.decimals,
          usdValue: tokenBalance * mockPrice,
          priceChange24h: mockChange,
        });
      }

      // Sort by USD value
      holdingsList.sort((a, b) => b.usdValue - a.usdValue);

      // Calculate total
      const solValue = solBalanceAmount * 200; // Mock SOL price
      const tokenValue = holdingsList.reduce((sum, h) => sum + h.usdValue, 0);

      const wallet = trackedWallets.find(w => w.address === address);

      return {
        address,
        label: wallet?.label || shortenAddress(address, 4),
        isConnected: wallet?.isConnected || false,
        solBalance: solBalanceAmount,
        holdings: holdingsList,
        totalValue: solValue + tokenValue,
      };
    } catch (e) {
      console.error(`Failed to fetch portfolio for ${address}:`, e);
      return null;
    }
  }, [trackedWallets]);

  // Fetch all portfolios
  useEffect(() => {
    if (trackedWallets.length === 0) {
      setLoading(false);
      return;
    }

    const fetchAllPortfolios = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          trackedWallets.map(w => fetchWalletPortfolio(w.address))
        );
        const validPortfolios = results.filter((p): p is WalletPortfolio => p !== null);

        // Set display data based on selected wallet
        if (selectedWallet) {
          const selected = validPortfolios.find(p => p.address === selectedWallet);
          if (selected) {
            setSolBalance(selected.solBalance);
            setHoldings(selected.holdings);
            setTotalValue(selected.totalValue);
          }
        } else if (validPortfolios.length > 0) {
          // Aggregate all portfolios
          const totalSol = validPortfolios.reduce((sum, p) => sum + p.solBalance, 0);
          const allHoldings = validPortfolios.flatMap(p => p.holdings);
          const aggregatedTotal = validPortfolios.reduce((sum, p) => sum + p.totalValue, 0);

          setSolBalance(totalSol);
          setHoldings(allHoldings);
          setTotalValue(aggregatedTotal);
        }
      } catch (e) {
        console.error('Failed to fetch portfolios:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPortfolios();
  }, [trackedWallets, selectedWallet, fetchWalletPortfolio]);

  const handleAddWallet = () => {
    setAddError(null);

    if (!newWalletAddress.trim()) {
      setAddError('Please enter a wallet address');
      return;
    }

    if (!isValidSolanaAddress(newWalletAddress.trim())) {
      setAddError('Invalid Solana address');
      return;
    }

    const updated = addTrackedWallet(newWalletAddress.trim(), newWalletLabel.trim() || undefined);
    setTrackedWallets(updated);
    setNewWalletAddress('');
    setNewWalletLabel('');
    setShowAddWallet(false);
  };

  const handleRemoveWallet = (address: string) => {
    const updated = removeTrackedWallet(address);
    setTrackedWallets(updated);
    if (selectedWallet === address) {
      setSelectedWallet(null);
    }
  };

  if (!connected) {
    return (
      <div style={{ paddingTop: '48px', textAlign: 'center' }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 24px',
          background: 'var(--surface-secondary)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '32px',
        }}>
          👛
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
          Connect Wallet
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
          Connect your wallet to view your portfolio
        </p>
        <button
          onClick={() => setVisible(true)}
          className="btn btn-primary"
          style={{ minWidth: '160px' }}
        >
          Connect Wallet
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '48px', textAlign: 'center' }}>
        <div
          className="animate-spin"
          style={{
            width: '40px',
            height: '40px',
            margin: '0 auto 16px',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
          }}
        />
        <p style={{ color: 'var(--text-secondary)' }}>Loading portfolio...</p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '16px' }}>
      {/* Wallet Selector */}
      {trackedWallets.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Wallets ({trackedWallets.length})
            </h3>
            <button
              onClick={() => setShowAddWallet(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                background: 'var(--accent-muted)',
                color: 'var(--accent)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
            {/* All wallets option */}
            <button
              onClick={() => setSelectedWallet(null)}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: 500,
                background: selectedWallet === null ? 'var(--accent)' : 'var(--bg-secondary)',
                color: selectedWallet === null ? '#000' : 'var(--text-primary)',
                border: '1px solid',
                borderColor: selectedWallet === null ? 'var(--accent)' : 'var(--border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              All ({trackedWallets.length})
            </button>

            {trackedWallets.map((wallet) => (
              <button
                key={wallet.address}
                onClick={() => setSelectedWallet(wallet.address)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: 500,
                  background: selectedWallet === wallet.address ? 'var(--accent)' : 'var(--bg-secondary)',
                  color: selectedWallet === wallet.address ? '#000' : 'var(--text-primary)',
                  border: '1px solid',
                  borderColor: selectedWallet === wallet.address ? 'var(--accent)' : 'var(--border)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {wallet.isConnected && (
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: selectedWallet === wallet.address ? '#000' : 'var(--success)',
                  }} />
                )}
                {wallet.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add Wallet Modal */}
      {showAddWallet && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
          onClick={() => setShowAddWallet(false)}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '20px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Track Wallet
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Wallet Address
              </label>
              <input
                type="text"
                value={newWalletAddress}
                onChange={(e) => setNewWalletAddress(e.target.value)}
                placeholder="Enter Solana wallet address"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '13px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Label (optional)
              </label>
              <input
                type="text"
                value={newWalletLabel}
                onChange={(e) => setNewWalletLabel(e.target.value)}
                placeholder="e.g. Trading, Whale, Friend"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '13px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {addError && (
              <p style={{ fontSize: '12px', color: 'var(--error)', marginBottom: '12px' }}>
                {addError}
              </p>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowAddWallet(false)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddWallet}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                Add Wallet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Header */}
      <div className="card" style={{ textAlign: 'center', marginBottom: '16px' }}>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
          {selectedWallet ? 'Wallet Balance' : 'Total Balance'}
        </p>
        <h1 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px' }}>
          ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </h1>
        {selectedWallet ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
              {shortenAddress(selectedWallet, 6)}
            </p>
            {!trackedWallets.find(w => w.address === selectedWallet)?.isConnected && (
              <button
                onClick={() => handleRemoveWallet(selectedWallet)}
                style={{
                  padding: '2px 6px',
                  fontSize: '10px',
                  background: 'var(--error)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
            {trackedWallets.length} wallet{trackedWallets.length !== 1 ? 's' : ''} tracked
          </p>
        )}
      </div>

      {/* SOL Balance */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #9945FF, #14F195)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
            }}>
              ◎
            </div>
            <div>
              <p style={{ fontSize: '15px', fontWeight: 600 }}>Solana</p>
              <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>SOL</p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '15px', fontWeight: 600 }}>
              {solBalance.toFixed(4)} SOL
            </p>
            <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
              ${(solBalance * 200).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Token Holdings */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
          Token Holdings ({holdings.length})
        </h3>

        {holdings.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '24px' }}>
            <p style={{ color: 'var(--text-tertiary)' }}>No token holdings found</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {holdings.map((holding) => (
              <button
                key={holding.mint}
                onClick={() => navigate(`/${holding.mint}`)}
                className="card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'var(--surface-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                  }}>
                    {holding.symbol.charAt(0)}
                  </div>
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: 600 }}>{holding.symbol}</p>
                    <p style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      {holding.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} tokens
                    </p>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '14px', fontWeight: 600 }}>
                    ${holding.usdValue.toFixed(2)}
                  </p>
                  <p style={{
                    fontSize: '11px',
                    color: holding.priceChange24h >= 0 ? 'var(--success)' : 'var(--error)',
                  }}>
                    {holding.priceChange24h >= 0 ? '+' : ''}{holding.priceChange24h.toFixed(1)}%
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <button
          onClick={() => navigate('/')}
          className="btn btn-secondary"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          Find Tokens
        </button>
        <button
          onClick={() => {/* Add transaction history */}}
          className="btn btn-secondary"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
          History
        </button>
      </div>
    </div>
  );
}
